import React, { FC, ReactNode, useContext, useEffect, useState } from 'react'

import { ChevronDownIcon, PlusIcon } from 'lucide-react'

import { ClassesSkeleton } from '../../empty/LineItemsEmpty/LineItemsEmpty'
import { PaymentActions } from '../../PaymentActions/PaymentActions'
import { PaymentStatus } from '../../../../../../pages/receipt/components/PaymentStatus/PaymentStatus'
import { PaymentAllActions } from '../../PaymentAllActions/PaymentAllActions'
import { PriceItem } from '../../../../../common/price/PriceItem'
import { Button } from '../../../../../ui/button'

import { getRegistrationEventDataInDb } from '../../../../../../helpers/registration-on-event'
import {
  getCurrency,
  getPaymentMethodsFormatted,
  getTotalPrices,
  isPaymentPending,
  isRefundPending,
  ITotalPrices,
} from '../../../../../../helpers/payment'
import { useEventRiderTeamMembers } from '../../../../../../hooks/useEventRiderTeamMembers'
import { usePaymentActions } from '../../../../../../hooks/usePaymentActions'
import { getFloatPrice } from '../../../../../../helpers/price'
import { cn } from '../../../../../../lib/utils'
import useToasterHelper from '../../../../../../helpers/ToasterHelper'

import { useAppSelector } from '../../../../../../store/hooks'
import { selectProfileData } from '../../../../../../store/user/userSlice'

import FirestoreService from '../../../../../../services/firestoreService'
import { HandleModalContext } from '../../../../../../layout/mainlayout/MainLayout'

import { IRegistrationFeesInterface } from '../../../../../../models/registration-fees/registrationFees.interface'
import { IEventReviewPublish } from '../../../../../../models/event-review-publish/event-review-publish.interface'
import { IPaymentsCards, IUserInterface } from '../../../../../../models/users/user.interface'
import { IRiderTeamMemberInterface } from '../../../../../../models/rider-team-member/riderTeamMember.interface'

import { PAYMENT_STATUS } from '../../../../../../types/payment'
import { MESSAGES_CONST } from '../../../../../../const/messages-const'
import { CONST } from '../../../../../../const/const'
import { MODAL_CONSTS } from '../../../../../../const/modal-const'
import { REGISTRAR_TABS } from '../../../RegistrarInfoModal/RegistrarInfoModal'
import { RIDER_TABS } from '../../../RiderInfoModal/RiderInfoModal'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

interface FeesTabProps {
  isOpenedDefault?: boolean
  type?: 'overview' | 'registrar'
  className?: string
  isCollapsible?: boolean
  registrarId: string | null
  registrationId: string
  eventId: string
  publishedEvent?: IEventReviewPublish | null
  user: IUserInterface | null
  organizer: IUserInterface | null
  onActionClick?: () => void
  actionIcon?: ReactNode
}

export const FeesTab: FC<FeesTabProps> = ({
  isOpenedDefault,
  type,
  className,
  isCollapsible,
  registrarId,
  registrationId,
  eventId,
  publishedEvent,
  user,
  organizer,
  onActionClick,
  actionIcon,
}) => {
  const handleModalContext = useContext(HandleModalContext)
  const toastFunctions = useToasterHelper()

  const { eventTeamMembers } = useEventRiderTeamMembers(eventId || '')
  const { onScratch, onRefund, onPayByCard, onMarkPaid, onRecipientClick, onUndoMarkMaid } =
    usePaymentActions()

  const profileData = useAppSelector(selectProfileData)

  const [loading, setLoading] = useState(true)
  const [isOpen, setIsOpen] = useState(!isCollapsible || isOpenedDefault)
  const [fees, setFees] = useState<IRegistrationFeesInterface[]>([])
  const [total, setTotal] = useState<ITotalPrices>()
  const [showAdjustField, setShowAdjustField] = useState<number | null>(null)
  const [showRefundField, setShowRefundField] = useState<number | null>(null)
  const [filteredFees, setFilteredFees] = useState<IRegistrationFeesInterface[]>([])
  const [paymentsCards, setPaymentsCards] = useState<IPaymentsCards>({ CARDS: [], ACH: [] })

  useEffect(() => {
    let filtered

    const sorted = fees.sort(
      (a, b) => new Date(b.created as Date).getTime() - new Date(a.created as Date).getTime()
    )

    if (type === 'overview') {
      filtered = sorted.filter(
        (fee) =>
          isPaymentPending(fee.isScratched, fee.paymentStatus) ||
          isRefundPending(
            fee.isScratched,
            fee.refundAmountAdded,
            fee.paymentStatus,
            fee.paymentType
          )
      )
    } else {
      filtered = sorted
    }
    setFilteredFees(filtered)
  }, [fees, type])

  useEffect(() => {
    const getData = async () => {
      if (!user?.id) return

      try {
        setLoading(true)

        const { registrationsFees } = await getRegistrationEventDataInDb({
          registrationId,
          ownerId: user.id,
          type: ['fees'],
        })

        const { totalFees } = getTotalPrices({
          registrationsEntries: [],
          registrationsFees: registrationsFees,
          registrationsTickets: [],
          isChargeSalesTax: publishedEvent?.EventPaymentSettings?.chargeSalesTax ?? false,
        })

        let userEmail
        // If it is an organizer, we show the riders' cards; if it is the registrar, we show the registrar's cards.
        if (organizer?.id === profileData.id) userEmail = user.userEmail
        else userEmail = profileData.userEmail

        const payments = await getPaymentMethodsFormatted(userEmail, organizer?.userStripeAccountId)

        setFees(registrationsFees)
        setTotal(totalFees)
        setPaymentsCards(payments)
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }
    getData().then()
  }, [
    organizer?.id,
    organizer?.userStripeAccountId,
    profileData.id,
    profileData.userEmail,
    publishedEvent,
    registrationId,
    user?.id,
    user?.userEmail,
  ])

  const refundPending = (items: IRegistrationFeesInterface[]) => {
    return (
      items?.filter((fee) =>
        isRefundPending(fee.isScratched, fee.refundAmountAdded, fee.paymentStatus, fee.paymentType)
      )?.length || 0
    )
  }

  const paymentPending = filteredFees?.filter((fee) =>
    isPaymentPending(fee.isScratched, fee.paymentStatus)
  )

  const haveScratched = filteredFees?.filter((fee) => !fee.isScratched) ?? []

  const haveNotPaid =
    filteredFees?.filter((fee) => fee.paymentStatus === PAYMENT_STATUS.DEFAULT) ?? []

  const onSaveAdjust = async (adjustItem: IRegistrationFeesInterface, adjustedValue: number) => {
    try {
      await FirestoreService.updateItem(COLLECTIONS.REGISTRATION_FEES.NAME, adjustItem.id, {
        feesPrice: adjustedValue,
        originalFeesPrice: adjustItem.originalFeesPrice ?? adjustItem.feesPrice,
      })

      const { registrationsFees } = await getRegistrationEventDataInDb({
        registrationId,
        ownerId: user?.id,
        type: ['fees'],
      })
      setFees(registrationsFees)

      setShowAdjustField(null)

      toastFunctions.success({
        message: `Adjusted successfully`,
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onSaveRefund = async (refundItems: IRegistrationFeesInterface[], refundValue?: number) => {
    if (organizer?.id && user?.id) {
      const response = await onRefund({
        refundRegistrationsByDayItems: [],
        refundFeesItems: refundItems,
        refundTicketsItems: [],
        registrationDocId: registrationId,
        publishedEvent: publishedEvent,
        ownerId: user?.id,
        refundValue: refundValue,
      })
      setShowRefundField(null)
      if (response?.registrationsFeesUpdated) setFees(response.registrationsFeesUpdated)
    }
  }

  const onSaveAssign = async (
    assignItem: IRegistrationFeesInterface,
    teamMember: IRiderTeamMemberInterface
  ) => {
    try {
      await FirestoreService.updateItem(COLLECTIONS.REGISTRATION_FEES.NAME, assignItem.id, {
        recipientId: teamMember?.teamMemberId || null,
        recipientName: teamMember?.teamMemberName || null,
        recipientProfilePicture: teamMember?.teamMemberProfilePicture || null,
      })

      const { registrationsFees } = await getRegistrationEventDataInDb({
        registrationId,
        ownerId: user?.id,
        type: ['fees'],
      })
      setFees(registrationsFees)

      toastFunctions.success({
        message: `Assigned successfully`,
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDeleteItem = async (id: string) => {
    try {
      await FirestoreService.deleteItem(COLLECTIONS.REGISTRATION_FEES.NAME, id)

      const { registrationsFees } = await getRegistrationEventDataInDb({
        registrationId,
        ownerId: user?.id,
        type: ['fees'],
      })

      setFees(registrationsFees)

      toastFunctions.success({
        message: 'Fee Deleted successfully',
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctions.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onAddFees = () => {
    handleModalContext?.handleModal(true, MODAL_CONSTS.ADD_FEES, {
      ...handleModalContext?.dataToPassOn,
      eventId: eventId,
      registrationDocId: registrationId,
      user: user,
      onSave: (newFees: IRegistrationFeesInterface[]) => {
        setFees([...fees, ...newFees])
      },
    })
  }

  const onUpdate = (registrationsFeesUpdated: IRegistrationFeesInterface[]) => {
    const updated = fees.map((fee) => {
      const current = registrationsFeesUpdated?.find((item) => item.id === fee.id)
      if (current?.id) {
        return { ...fee, ...current }
      } else {
        return fee
      }
    })
    setFees(updated)
  }

  const currency = getCurrency(publishedEvent?.EventPaymentSettings?.currency)

  if (loading) return <ClassesSkeleton />

  return (
    <div className={cn('rounded-lg border border-black/10 px-7 py-3', className)}>
      <div className="flex gap-2 flex-wrap items-center justify-between">
        <div>
          <p className="text-base font-medium">Fees</p>
          {type === 'overview' ? (
            <p className="text-xs text-black/50">
              {filteredFees?.[0] ? `${filteredFees.length} issues` : 'No issues'}
            </p>
          ) : (
            <p className="text-xs text-black/50">
              {filteredFees?.length || 0} items,{' '}
              {getFloatPrice(`${currency}${(total?.paid || 0) / 100}`)} total paid
            </p>
          )}
        </div>
        <div className="text-right flex gap-3 items-center justify-end ml-auto">
          {((type === 'registrar' && !filteredFees?.[0]) ||
            (type !== 'overview' && !filteredFees?.[0])) && (
            <Button
              variant="link"
              type="button"
              className="rounded-full  w-[30px] h-[30px] text-black bg-black/5 hover:opacity-70 transition-all"
              onClick={onAddFees}
            >
              <PlusIcon className="w-4" />
            </Button>
          )}
          {type === 'overview' ? (
            <Button
              variant="link"
              type="button"
              className="rounded-full p-0 hover:opacity-70"
              onClick={onActionClick}
            >
              {actionIcon}
            </Button>
          ) : (
            filteredFees?.[0] && (
              <>
                <div className="flex gap-2 items-center justify-end flex-wrap">
                  {refundPending(filteredFees) > 0 && (
                    <div className="flex items-center text-sm text-PurpleThemeColor bg-PurpleThemeColor/5 px-3 py-1 rounded-full">
                      {refundPending(filteredFees)} refund pending
                    </div>
                  )}
                  {paymentPending && paymentPending.length > 0 && (
                    <div className="flex items-center text-sm text-SeabiscuitRedThemeColor bg-SeabiscuitRedThemeColor/5 px-3 py-1 rounded-full">
                      {paymentPending.length} payment pending
                    </div>
                  )}
                </div>
                <PaymentAllActions
                  type="fee"
                  haveScratched={haveScratched?.length > 0}
                  isRefundPending={refundPending(filteredFees) > 0}
                  haveNotPaid={haveNotPaid.length > 0}
                  paymentsCards={paymentsCards}
                  organizer={organizer}
                  setPaymentsCards={setPaymentsCards}
                  user={user}
                  eventReviewPublish={publishedEvent}
                  onScratch={async () => {
                    const response = await onScratch({
                      registrationDocId: registrationId,
                      publishedEvent: publishedEvent,
                      user: user,
                      registrationsByDayScratchedItems: [],
                      feesScratchedItems: filteredFees.filter((fee) => !fee.isScratched),
                      ticketsScratchedItems: [],
                      isScratched: false,
                    })
                    if (response?.registrationsFeesUpdated)
                      onUpdate(response.registrationsFeesUpdated)
                  }}
                  onRefund={async () => {
                    const items = filteredFees.filter(
                      (fee) =>
                        fee.isScratched &&
                        !fee.refundAmountAdded &&
                        fee.paymentStatus === PAYMENT_STATUS.PAID
                    )
                    await onSaveRefund(items)
                  }}
                  onMarkPaidAction={async (values) => {
                    if (user) {
                      const response = await onMarkPaid({
                        markPaidRegistrationsByDayItems: [],
                        markPaidFeesItems: filteredFees.filter(
                          (fee) => !fee.paymentStatus && !fee.isScratched
                        ),
                        markPaidTicketsItems: [],
                        paymentType: values.type,
                        checkMemo: values.checkMemo,
                        checkNumber: values.checkNumber,
                        registrationDocId: registrationId,
                        user: user,
                        organizerId: organizer?.id || null,
                      })
                      if (response?.registrationsFeesUpdated)
                        onUpdate(response.registrationsFeesUpdated)
                    }
                  }}
                  onPayByCard={async (card) => {
                    if (user && registrarId) {
                      const response = await onPayByCard({
                        registrationsByDayItems: [],
                        ticketItems: [],
                        feeItems: filteredFees.filter(
                          (fee) => !fee.paymentStatus && !fee.isScratched
                        ),
                        card: card,
                        registrationDocId: registrationId,
                        user: user,
                        publishedEvent: publishedEvent,
                      })
                      if (response?.registrationsFeesUpdated)
                        onUpdate(response.registrationsFeesUpdated)
                    }
                  }}
                  onAddNew={onAddFees}
                />
              </>
            )
          )}
          {isCollapsible && (
            <Button
              variant="link"
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                'rounded-full duration-200 w-[30px] h-[30px] flex items-center justify-center shrink-0 hover:opacity-70 transition-all',
                isOpen
                  ? 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rotate-180'
                  : 'text-black bg-black/5'
              )}
            >
              <ChevronDownIcon className="size-4" />
            </Button>
          )}
        </div>
      </div>
      {isOpen && (
        <div
          className={cn(
            (!isCollapsible || isOpen) && filteredFees?.[0] && 'mt-3 border-t border-t-black/10'
          )}
        >
          {filteredFees?.[0]
            ? filteredFees?.map((fee, index) => {
                return (
                  <div
                    key={index}
                    className={cn(
                      'flex gap-2 flex-wrap items-center justify-between py-4',
                      filteredFees.length > index + 1 && 'border-b border-b-black/10'
                    )}
                  >
                    <div>
                      <p className="text-sm flex items-center gap-2 flex-wrap mb-1">
                        <span className={cn(fee.isScratched && 'line-through')}>
                          {fee.feesTitle}
                        </span>
                      </p>

                      <div className="text-xs text-black/50">
                        {fee.selectedUnitsCount > 0 && `${fee.selectedUnitsCount} units,`}{' '}
                        <PriceItem
                          isRefunded={fee.refundAmountAdded}
                          amountRefunded={fee.amountRefunded}
                          price={fee.feesPrice || 0}
                          originalPrice={fee.originalFeesPrice || 0}
                          showAdjustField={showAdjustField === index}
                          onCancelAdjust={() => setShowAdjustField(null)}
                          onSaveAdjust={(value) => onSaveAdjust(fee, value)}
                          showRefundField={showRefundField === index}
                          onCancelRefund={() => setShowRefundField(null)}
                          onSaveRefund={(value) => onSaveRefund([fee], value)}
                          currency={currency}
                        />
                      </div>
                    </div>
                    <div className="text-right ml-auto">
                      {type !== 'overview' && (
                        <PaymentActions
                          isRefundPending={refundPending([fee]) > 0}
                          eventTeamMembers={eventTeamMembers}
                          paymentStatus={fee.paymentStatus}
                          paymentType={fee.paymentType}
                          isScratched={fee.isScratched ?? false}
                          isRefunded={fee.refundAmountAdded ?? false}
                          paymentsCards={paymentsCards}
                          organizer={organizer}
                          user={user}
                          eventReviewPublish={publishedEvent}
                          setPaymentsCards={setPaymentsCards}
                          onScratch={async (type) => {
                            const response = await onScratch({
                              registrationDocId: registrationId,
                              publishedEvent: publishedEvent,
                              user: user,
                              registrationsByDayScratchedItems: [],
                              feesScratchedItems: [fee],
                              ticketsScratchedItems: [],
                              isScratched: type,
                            })
                            if (response?.registrationsFeesUpdated)
                              onUpdate(response.registrationsFeesUpdated)
                          }}
                          onMarkPaidAction={async (values) => {
                            if (user) {
                              const response = await onMarkPaid({
                                markPaidRegistrationsByDayItems: [],
                                markPaidFeesItems: [fee],
                                markPaidTicketsItems: [],
                                paymentType: values.type,
                                checkMemo: values.checkMemo,
                                checkNumber: values.checkNumber,
                                registrationDocId: registrationId,
                                user: user,
                                organizerId: organizer?.id || null,
                              })
                              if (response?.registrationsFeesUpdated)
                                onUpdate(response.registrationsFeesUpdated)
                            }
                          }}
                          onAdjust={() => setShowAdjustField(index)}
                          onRefund={() => setShowRefundField(index)}
                          onSaveAssign={(teamMember) => onSaveAssign(fee, teamMember)}
                          onPayByCard={async (card) => {
                            if (user && registrarId) {
                              const response = await onPayByCard({
                                registrationsByDayItems: [],
                                ticketItems: [],
                                feeItems: [fee],
                                card: card,
                                registrationDocId: registrationId,
                                user: user,
                                publishedEvent: publishedEvent,
                              })

                              if (response?.registrationsFeesUpdated)
                                onUpdate(response.registrationsFeesUpdated)
                            }
                          }}
                          registrationDocId={registrationId}
                          onDelete={async () => await onDeleteItem(fee.id || '')}
                        />
                      )}
                      <div
                        className={cn(
                          'flex items-center text-sm',
                          isRefundPending(
                            fee.isScratched,
                            fee.refundAmountAdded,
                            fee.paymentStatus,
                            fee.paymentType
                          )
                            ? 'text-PurpleThemeColor'
                            : isPaymentPending(fee.isScratched, fee.paymentStatus)
                              ? 'text-SeabiscuitRedThemeColor'
                              : ' text-black/50',
                          type === 'overview' ? 'rounded-full py-1 px-3' : 'bg-transparent'
                        )}
                      >
                        <PaymentStatus
                          isAdjusted={!!fee.originalFeesPrice}
                          isRefunded={fee.refundAmountAdded || false}
                          isScratched={fee.isScratched || false}
                          paymentStatus={fee.paymentStatus}
                          scratchedDate={fee.scratchedDate || null}
                          refundedDate={fee.refundedDate || null}
                          paidDate={fee.paidDate || null}
                          paymentType={fee.paymentType}
                          ownerId={fee.ownerId || null}
                          ownerName={fee.ownerName || null}
                          recipientId={fee.recipientId || null}
                          recipientName={fee.recipientName || null}
                          userId={user?.id}
                          onRecipientClick={(value) =>
                            onRecipientClick({
                              eventId,
                              registrarId,
                              registrationId,
                              defaultActiveTab: REGISTRAR_TABS.TEAM.value,
                              defaultOpenedPrimaryAccordion: value,
                              defaultOpenedSecondaryAccordion: RIDER_TABS.FEES.value as 'fees',
                            })
                          }
                          onUndoMarkMaid={async () => {
                            if (fee.id && user) {
                              const response = await onUndoMarkMaid({
                                eventId: eventId,
                                registrationId: registrationId,
                                paymentType: fee.paymentType,
                                registrationsByDay: [],
                                fees: [fee],
                                tickets: [],
                                user: user,
                                publishedEvent: publishedEvent,
                              })

                              if (response?.registrationsFeesUpdated)
                                onUpdate(response.registrationsFeesUpdated)
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })
            : type === 'registrar' && (
                <div className="pt-4 mt-4 border-t border-t-black/10">
                  <div className=" text-black/50 text-xs bg-black/[2%] h-[112px] rounded-lg flex items-center justify-center p-4">
                    No fees have been added to this entry
                  </div>
                </div>
              )}
        </div>
      )}
    </div>
  )
}
